/**
 * Criar / editar uma entrada da base de conhecimento (spec 2026-10-08 §3).
 *
 * Usa o `Modal` do `@lume/ui-components` (primitivo de diálogo do DS, inline — não portal),
 * mesmo molde de `PersonaCreateModal`. O componente só é montado enquanto aberto (o pai
 * monta/desmonta), então o estado do form nasce limpo a cada abertura sem `useEffect` de
 * reset.
 *
 * Editar envia PATCH só com os campos que mudaram (`buildUpdateBody`); nada mudou ⇒ fecha
 * sem request. Por que importa: o servidor recarimba `ratifiedBy/At` quando
 * `title|body|kind|keywords` mudam — mandar o objeto inteiro faria uma troca de prioridade
 * parecer, no audit, uma reescrita do conteúdo.
 */
import { Modal } from '@lume/ui-components';
import { X } from 'lucide-react';
import { useState } from 'react';

import { VERTICAL_LABELS } from '../../personas/persona-labels';

import {
  addKeyword,
  buildCreateBody,
  buildUpdateBody,
  FORM_VAZIO,
  formFromEntry,
  KIND_EXPLICACAO,
  KIND_LABELS,
  LIMITES,
  validateKnowledgeForm,
  VERTICAIS,
} from './knowledge-form';
import { useCreateKnowledgeEntry, useUpdateKnowledgeEntry } from './use-knowledge-entries';

import type { KnowledgeFormErrors, KnowledgeFormValues } from './knowledge-form';
import type { KnowledgeEntryDto, KnowledgeKind, KnowledgeVertical } from './types';

const KINDS: KnowledgeKind[] = ['fact', 'rule'];

export function KnowledgeEntryFormModal({
  entry,
  onClose,
}: {
  /** `null` = criar; entrada = editar. */
  readonly entry: KnowledgeEntryDto | null;
  readonly onClose: () => void;
}): JSX.Element {
  const [values, setValues] = useState<KnowledgeFormValues>(
    entry === null ? FORM_VAZIO : formFromEntry(entry),
  );
  const [pendingKeyword, setPendingKeyword] = useState('');
  const [errors, setErrors] = useState<KnowledgeFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const create = useCreateKnowledgeEntry();
  const update = useUpdateKnowledgeEntry();
  const pending = create.isPending || update.isPending;

  const set = <K extends keyof KnowledgeFormValues>(k: K, v: KnowledgeFormValues[K]): void => {
    setValues((prev) => ({ ...prev, [k]: v }));
  };

  const commitKeyword = (): void => {
    set('keywords', addKeyword(values.keywords, pendingKeyword));
    setPendingKeyword('');
  };

  const onError = (): void => {
    setSubmitError('Não foi possível salvar a entrada. Confira os campos e tente de novo.');
  };

  const handleConfirm = (): void => {
    if (pending) return;
    // Palavra digitada e não confirmada com Enter ainda conta — perdê-la em silêncio é pior.
    const v =
      pendingKeyword.trim() === ''
        ? values
        : { ...values, keywords: addKeyword(values.keywords, pendingKeyword) };
    const fieldErrors = validateKnowledgeForm(v);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;
    setSubmitError(null);

    if (entry === null) {
      create.mutate(buildCreateBody(v), { onSuccess: onClose, onError });
      return;
    }
    const body = buildUpdateBody(entry, v);
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    update.mutate({ id: entry.id, body }, { onSuccess: onClose, onError });
  };

  const criando = entry === null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={criando ? 'Nova entrada' : 'Editar entrada'}
      confirmLabel={pending ? 'Salvando…' : criando ? 'Criar entrada' : 'Salvar alterações'}
      cancelLabel="Cancelar"
      onConfirm={handleConfirm}
      maxWidth="640px"
    >
      <div className="flex flex-col gap-4">
        <div className="field">
          <label className="field__label" htmlFor="kb-title">
            Título
          </label>
          <input
            id="kb-title"
            className="input"
            value={values.title}
            maxLength={LIMITES.tituloMax}
            aria-invalid={errors.title !== undefined}
            placeholder="ex.: Horário de funcionamento"
            onChange={(e) => {
              set('title', e.target.value);
            }}
          />
          {errors.title !== undefined && <span className="field__error">{errors.title}</span>}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="kb-body">
            Texto
          </label>
          <textarea
            id="kb-body"
            className="textarea"
            rows={5}
            value={values.body}
            maxLength={LIMITES.textoMax}
            aria-invalid={errors.body !== undefined}
            placeholder="O que a IA deve saber ou fazer, em frases simples."
            onChange={(e) => {
              set('body', e.target.value);
            }}
          />
          {errors.body !== undefined && <span className="field__error">{errors.body}</span>}
        </div>

        <fieldset className="field">
          <legend className="field__label mb-1">Tipo</legend>
          <div className="flex flex-col gap-2">
            {KINDS.map((k) => (
              <label key={k} className="radio items-start">
                <input
                  type="radio"
                  name="kb-kind"
                  value={k}
                  checked={values.kind === k}
                  onChange={() => {
                    set('kind', k);
                  }}
                />
                <span className="radio__box mt-0.5" aria-hidden />
                <span>
                  <span className="font-medium">{KIND_LABELS[k]}</span>
                  <span className="text-text-secondary"> — {KIND_EXPLICACAO[k]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="field">
          <span className="field__label" id="kb-keywords-label">
            Palavras-chave
          </span>
          <span className="field__hint">
            A IA consulta esta entrada quando a conversa contém uma destas palavras. Maiúsculas e
            acentos não fazem diferença.
          </span>
          {values.keywords.length > 0 && (
            <ul className="mt-1 flex flex-wrap gap-1.5" aria-labelledby="kb-keywords-label">
              {values.keywords.map((k) => (
                <li key={k} className="chip cursor-default">
                  {k}
                  <button
                    type="button"
                    className="text-text-secondary hover:text-text-primary"
                    aria-label={`Remover ${k}`}
                    onClick={() => {
                      set(
                        'keywords',
                        values.keywords.filter((x) => x !== k),
                      );
                    }}
                  >
                    <X size={12} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-1 flex gap-2">
            <input
              type="text"
              className="input flex-1"
              value={pendingKeyword}
              maxLength={LIMITES.palavraMax}
              aria-label="Adicionar palavra-chave"
              placeholder="Digite e aperte Enter"
              onChange={(e) => {
                setPendingKeyword(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  commitKeyword();
                }
              }}
            />
            <button type="button" className="btn btn--secondary btn--md" onClick={commitKeyword}>
              Adicionar
            </button>
          </div>
          {errors.keywords !== undefined && <span className="field__error">{errors.keywords}</span>}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="field">
            <label className="field__label" htmlFor="kb-priority">
              Prioridade
            </label>
            <input
              id="kb-priority"
              type="number"
              className="input"
              min={0}
              max={LIMITES.prioridadeMax}
              step={1}
              value={values.priority}
              aria-invalid={errors.priority !== undefined}
              onChange={(e) => {
                set('priority', e.target.value);
              }}
            />
            <span className="field__hint">
              De 0 a 1000. Quando várias entradas servem, a de número maior vem primeiro.
            </span>
            {errors.priority !== undefined && (
              <span className="field__error">{errors.priority}</span>
            )}
          </div>

          <div className="field">
            <label className="field__label" htmlFor="kb-vertical">
              Quando vale
            </label>
            <select
              id="kb-vertical"
              className="input"
              value={values.vertical ?? ''}
              onChange={(e) => {
                set(
                  'vertical',
                  e.target.value === '' ? null : (e.target.value as KnowledgeVertical),
                );
              }}
            >
              <option value="">Sempre (em qualquer atendimento)</option>
              {VERTICAIS.map((v) => (
                <option key={v} value={v}>
                  Só em {VERTICAL_LABELS[v]}
                </option>
              ))}
            </select>
            <span className="field__hint">Restringe a entrada ao tipo de playbook escolhido.</span>
          </div>
        </div>

        <label className="switch">
          <input
            type="checkbox"
            checked={values.enabled}
            onChange={(e) => {
              set('enabled', e.target.checked);
            }}
            aria-label="Entrada ativa"
          />
          <span className="switch__track" aria-hidden />
          <span>
            Entrada ativa
            <span className="text-text-secondary"> — desligada, a IA não a usa</span>
          </span>
        </label>

        {submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
}
